import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import ImageMarker from '@/components/CustomComponent/ReactImageMarker';
import { COMMON_TEXT } from '@/helpers/common-text';
import {
  MEMORY_STORE,
  QUERY_STRING_STORE,
  SENSOR_TYPE,
} from '@/helpers/constants';
import { CustomSensorMarker } from '@/helpers/sensor-marker';
import {
  transformPositionToMarkerCoordinate,
} from '@/helpers/utility';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Message } from 'primereact/message';
import { SelectButton } from 'primereact/selectbutton';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { fetchSensorDataNow, fetchSensorDataThreshold } from './query-request';
import { isEqual } from 'lodash';
import './AirQualityNow.less';



const SENSOR_TYPE_OPTIONS = [
  {
    label: COMMON_TEXT.TEMPERATURE,
    value: SENSOR_TYPE.TEMPERATURE,
  },
  {
    label: COMMON_TEXT.HUMIDITY,
    value: SENSOR_TYPE.HUMIDITY,
  },
  {
    label: COMMON_TEXT.CARBON_DIOXIDE,
    value: SENSOR_TYPE.CARBON_DIOXIDE,
  },
  {
    label: COMMON_TEXT.NOISE_LEVEL,
    value: SENSOR_TYPE.NOISE_LEVEL,
  },
];

@connect(state => ({
  querystring: state.querystring,
  sessionStore: state.session,
}))
class AirQualityNow extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sensorData: [],
      isLoadingData: false,
      floorMapUrl: '',
      floorMapError: false,
      selectedSensorType: SENSOR_TYPE.TEMPERATURE,
      sensorDataThreshold: {},
    };
    this._isMounted = false; // Flag to track the mounted status
  }

  validateFloorMapUrl(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = () => reject(url);
      img.src = url;
    });
  }

  getFloorMapUrl = floorId => {
    const floors = this.props?.sessionStore[MEMORY_STORE.FLOORS] || [];
    const floor = floors.find(floor => floor.floor_id === floorId);
    const floorMapUrl = floor ? floor.floor_map_url : '';
    return floorMapUrl;
  };

  setFloorMap = async floorId => {
    const floors = this.props?.sessionStore[MEMORY_STORE.FLOORS] || [];
    const floor = floors.find(floor => floor.floor_id === floorId);
    const floorMapUrl = floor ? floor.floor_map_url : '';
    try {
      await this.validateFloorMapUrl(floorMapUrl);
      if (this._isMounted) {
        this.setState({ floorMapError: false, floorMapUrl: floorMapUrl });
      }
    } catch (error) {
      if (this._isMounted) {
        this.setState({ floorMapError: true, floorMapUrl: '' });
      }
    }
  };

  componentDidMount() {
    this._isMounted = true; // Set the flag to true when the component
    const { isSignageMode, signageModeData } = this.props;
    if (isSignageMode) {
      const { data, floor, selectedSensorType } = signageModeData;
      const { sensorData, sensorDataThreshold } = data;
      this.setState({
        sensorData: sensorData,
        sensorDataThreshold: sensorDataThreshold,
        selectedSensorType: selectedSensorType,
        floor,
        floorMapUrl: this.getFloorMapUrl(floor),
      });
    } else {
      const { querystring, sensorDataThreshold } = this.props;
      const floor = querystring?.[QUERY_STRING_STORE.SELECT_BOX_FLOOR]?.[0];
      if (floor) {
        this.setFloorMap(floor);
        this.setState(
          { floor, sensorDataThreshold: sensorDataThreshold ?? {} },
          () => {
            this.getAllData();
          }
        );
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false; // Set the flag to false when the component unmounts
  }

  componentDidUpdate(prevProps) {
    const { querystring, isSignageMode, signageModeData, sensorDataThreshold } =
      this.props;
    const {
      querystring: prevQuerystring,
      signageModeData: prevSignageModeData,
      sensorDataThreshold: prevSensorDataThreshold,
    } = prevProps;
    if (isSignageMode) {
      const { data, floor } = signageModeData;
      const { data: prevData } = prevSignageModeData;
      const { sensorData, sensorDataThreshold } = data;
      const {
        sensorData: prevSensorData,
        sensorDataThreshold: prevSensorDataThreshold,
      } = prevData;
      const isSignageModeDataChanged =
        !isEqual(sensorData, prevSensorData) ||
        !isEqual(sensorDataThreshold, prevSensorDataThreshold);
      if (isSignageModeDataChanged) {
        this.setState({
          sensorData: sensorData,
          sensorDataThreshold: sensorDataThreshold,
          floorMapUrl: this.getFloorMapUrl(floor),
        });
      }
    } else {
      const floor = querystring?.[QUERY_STRING_STORE.SELECT_BOX_FLOOR]?.[0];
      const prevFloor =
        prevQuerystring?.[QUERY_STRING_STORE.SELECT_BOX_FLOOR]?.[0];
      const isFloorChanged = floor !== prevFloor;
      if (isFloorChanged) {
        this.setFloorMap(floor);
        this.setState(
          { floor, sensorDataThreshold: sensorDataThreshold ?? {} },
          () => {
            this.getAllData();
          }
        );
      }
      const isSensorDataThresholdChanged =
        sensorDataThreshold !== prevSensorDataThreshold;
      if (isSensorDataThresholdChanged) {
        this.setState({
          sensorDataThreshold: sensorDataThreshold ?? {},
        });
      }
    }
  }

  getAllData = async () => {
    const { floor } = this.props;
    if (!floor) {
      return;
    }
    this.setState({ isLoadingData: true });

    // Fetch both sensor data and thresholds in parallel
    const [{ sensorData }, sensorDataThreshold] = await Promise.all([
      this.getSensorData({ floor }),
      this.getSensorDataThreshold({ floor })
    ]);

    this.setState({
      isLoadingData: false,
      sensorData,
      sensorDataThreshold,
    });
  };

  getSensorData = async ({ floor }) => {
    let sensorDataRes = await fetchSensorDataNow({
      floorId: floor,
    });

    let sensorData = [];
    if (sensorDataRes && sensorDataRes.length > 0) {
      sensorData = sensorDataRes.map(item => ({
        position: {
          x: parseFloat(item?.position_x),
          y: parseFloat(item?.position_y),
        },
        data: item ?? null,
      }));
    }
    return { sensorData };
  };

  getSensorDataThreshold = async ({ floor }) => {
    try {
      const thresholdRes = await fetchSensorDataThreshold({
        floorId: floor,
      });

      // Return the first threshold data or empty object if no data
      return thresholdRes && thresholdRes.length > 0 ? thresholdRes[0] : {};
    } catch (error) {
      console.error('Error fetching sensor data threshold:', error);
      return {};
    }
  };

  renderSensorDataSelectButton = () => {
    const { selectedSensorType } = this.state;
    return (
      <div className="card flex justify-content-center sensor-select-button">
        <SelectButton
          value={selectedSensorType}
          onChange={e => this.setState({ selectedSensorType: e.value })}
          options={SENSOR_TYPE_OPTIONS}
          allowEmpty={false}
        />
      </div>
    );
  };

  renderMessageError = () => {
    const { floor } = this.state;
    if (!floor) {
      return (
        <Message
          severity="error"
          text={COMMON_TEXT.FLOOR_NOT_SELECTED_PLEASE_SELECT}
        />
      );
    }
  };

  getSensorColors = (value, sensorType, sensorThresholds) => {
    if (sensorType === SENSOR_TYPE.TEMPERATURE) {
      return this.getSensorTemperatureColor(value, sensorThresholds);
    } else if (sensorType === SENSOR_TYPE.HUMIDITY) {
      return this.getSensorHumidityColor(value, sensorThresholds);
    } else if (sensorType === SENSOR_TYPE.CARBON_DIOXIDE) {
      return this.getSensorCO2Color(value, sensorThresholds);
    } else if (sensorType === SENSOR_TYPE.NOISE_LEVEL) {
      return this.getSensorNoiseColor(value, sensorThresholds);
    }
    return {}
  };

  getSensorTemperatureColor = (value, sensorThresholds) => {
    let backgroundColor = '#8E8E8E';
    let markerColor = '#8E8E8E';
    let colorName = 'gray';

    if (value === null || value === undefined) {
      return {
        backgroundColor,
        markerColor,
        colorName,
      }
    }

    const poorHigh = sensorThresholds?.temperature_poor_high ?? 27;
    const badHigh = sensorThresholds?.temperature_bad_high ?? 25;
    const fairHigh = sensorThresholds?.temperature_fair_high ?? 24;
    const fairLow = sensorThresholds?.temperature_fair_low ?? 23;
    const badLow = sensorThresholds?.temperature_bad_low ?? 20;
    const poorLow = sensorThresholds?.temperature_poor_low ?? 15;

    if (value > poorHigh) {
      backgroundColor = '#E16A7C';
      markerColor = '#E16A7C';
      colorName = 'red';
    } else if (value > badHigh) {
      backgroundColor = '#FFA14F';
      markerColor = '#FFA14F';
      colorName = 'orange';
    } else if (value > fairHigh) {
      backgroundColor = '#FFE135';
      markerColor = '#FFE135';
      colorName = 'yellow';
    } else if (value > fairLow) {
      backgroundColor = '#00B573';
      markerColor = '#00B573';
      colorName = 'green';
    } else if (value > badLow) {
      backgroundColor = '#6CC2BD';
      markerColor = '#6CC2BD';
      colorName = 'emeraldgreen';
    } else if (value > poorLow) {
      backgroundColor = '#5B98E6';
      markerColor = '#5B98E6';
      colorName = 'blue';
    } else {
      backgroundColor = '#252F55';
      markerColor = '#252F55';
      colorName = 'darkblue';
    }

    return {
      backgroundColor,
      markerColor,
      colorName,
    }
  }

  getSensorHumidityColor = (value, sensorThresholds) => {
    let backgroundColor = '#8E8E8E';
    let markerColor = '#8E8E8E';
    let colorName = 'gray';

    if (value === null || value === undefined) {
      return {
        backgroundColor,
        markerColor,
        colorName,
      }
    }

    const poorHigh = sensorThresholds?.humidity_poor_high ?? 80;
    const badHigh = sensorThresholds?.humidity_bad_high ?? 60;
    const fairHigh = sensorThresholds?.humidity_fair_high ?? 55;
    const fairLow = sensorThresholds?.humidity_fair_low ?? 35;
    const badLow = sensorThresholds?.humidity_bad_low ?? 25;
    const poorLow = sensorThresholds?.humidity_poor_low ?? 20;

    if (value > poorHigh || value < poorLow) {
      backgroundColor = '#E16A7C';
      markerColor = '#E16A7C';
      colorName = 'red';
    } else if (value > badHigh || value < badLow) {
      backgroundColor = '#FFA14F';
      markerColor = '#FFA14F';
      colorName = 'orange';
    } else if (value > fairHigh || value < fairLow) {
      backgroundColor = '#FFE135';
      markerColor = '#FFE135';
      colorName = 'yellow';
    } else {
      backgroundColor = '#00B573';
      markerColor = '#00B573';
      colorName = 'green';
    }

    return {
      backgroundColor,
      markerColor,
      colorName,
    }
  }

  getSensorCO2Color = (value, sensorThresholds) => {
    let backgroundColor = '#8E8E8E';
    let markerColor = '#8E8E8E';
    let colorName = 'gray';

    if (value === null || value === undefined) {
      return {
        backgroundColor,
        markerColor,
        colorName,
      }
    }

    const poorHigh = sensorThresholds?.carbon_dioxide_poor_high ?? 5000;
    const badHigh = sensorThresholds?.carbon_dioxide_bad_high ?? 2000;
    const fairHigh = sensorThresholds?.carbon_dioxide_fair_high ?? 1000;

    if (value > poorHigh) {
      backgroundColor = '#E16A7C';
      markerColor = '#E16A7C';
      colorName = 'red';
    } else if (value > badHigh) {
      backgroundColor = '#FFA14F';
      markerColor = '#FFA14F';
      colorName = 'orange';
    } else if (value > fairHigh) {
      backgroundColor = '#FFE135';
      markerColor = '#FFE135';
      colorName = 'yellow';
    } else {
      backgroundColor = '#00B573';
      markerColor = '#00B573';
      colorName = 'green';
    }

    return {
      backgroundColor,
      markerColor,
      colorName,
    }
  }

  getSensorNoiseColor = (value, sensorThresholds) => {
    let backgroundColor = '#8E8E8E';
    let markerColor = '#8E8E8E';
    let colorName = 'gray';

    if (value === null || value === undefined) {
      return {
        backgroundColor,
        markerColor,
        colorName,
      }
    }

    const poorHigh = sensorThresholds?.noise_level_poor_high ?? 105;
    const badHigh = sensorThresholds?.noise_level_bad_high ?? 100;
    const fairHigh = sensorThresholds?.noise_level_fair_high ?? 95;

    if (value > poorHigh) {
      backgroundColor = '#E16A7C';
      markerColor = '#E16A7C';
      colorName = 'red';
    } else if (value > badHigh) {
      backgroundColor = '#FFA14F';
      markerColor = '#FFA14F';
      colorName = 'orange';
    } else if (value > fairHigh) {
      backgroundColor = '#FFE135';
      markerColor = '#FFE135';
      colorName = 'yellow';
    } else {
      backgroundColor = '#00B573';
      markerColor = '#00B573';
      colorName = 'green';
    }

    return {
      backgroundColor,
      markerColor,
      colorName,
    }
  }

  updateColumnColor = ({ sensorDataList, sensorThresholds }) => {
    const sensorTypeList = SENSOR_TYPE_OPTIONS.map(item => item.value);
    sensorDataList.forEach(sensorData => {
      for (const sensorType of sensorTypeList) {
        const sensorValue = sensorData[sensorType];
        const tableColorKey = `${sensorType}_table_color`;
        const markerColorKey = `${sensorType}_marker_color`;

        const colors = this.getSensorColors(sensorValue, sensorType, sensorThresholds);

        sensorData[`${sensorType}_color`] = colors.colorName;
        sensorData[tableColorKey] = colors.backgroundColor;
        sensorData[markerColorKey] = colors.markerColor;
      }
    });
    return sensorDataList;
  };

  getIndicator = (className) => {
    return <div className={className}></div>;
  }

  getTemperatureStatusIndicator = (value, sensorThresholds) => {
    let className = 'status-indicator ';

    if (value === null || value === undefined) {
      className += 'status-gray';
    } else {
      const poorHigh = sensorThresholds?.temperature_poor_high ?? 27;
      const badHigh = sensorThresholds?.temperature_bad_high ?? 25;
      const fairHigh = sensorThresholds?.temperature_fair_high ?? 24;
      const fairLow = sensorThresholds?.temperature_fair_low ?? 23;
      const badLow = sensorThresholds?.temperature_bad_low ?? 20;
      const poorLow = sensorThresholds?.temperature_poor_low ?? 15;

      if (value > poorHigh) {
        className += 'status-red';
      } else if (value > badHigh) {
        className += 'status-orange';
      } else if (value > fairHigh) {
        className += 'status-yellow';
      } else if (value > fairLow) {
        className += 'status-green';
      } else if (value > badLow) {
        className += 'status-emeraldgreen';
      } else if (value > poorLow) {
        className += 'status-blue';
      } else {
        className += 'status-darkblue';
      }
    }

    return this.getIndicator(className);
  }

  getHumidityStatusIndicator = (value, sensorThresholds) => {
    let className = 'status-indicator ';

    if (value === null || value === undefined) {
      className += 'status-gray';
    } else {
      const poorHigh = sensorThresholds?.humidity_poor_high ?? 80;
      const badHigh = sensorThresholds?.humidity_bad_high ?? 60;
      const fairHigh = sensorThresholds?.humidity_fair_high ?? 55;
      const fairLow = sensorThresholds?.humidity_fair_low ?? 35;
      const badLow = sensorThresholds?.humidity_bad_low ?? 25;
      const poorLow = sensorThresholds?.humidity_poor_low ?? 20;

      if (value > poorHigh || value < poorLow) {
        className += 'status-red';
      } else if (value > badHigh || value < badLow) {
        className += 'status-orange';
      } else if (value > fairHigh || value < fairLow) {
        className += 'status-yellow';
      } else {
        className += 'status-green';
      }
    }

    return this.getIndicator(className);
  }

  getCO2StatusIndicator = (value, sensorThresholds) => {
    let className = 'status-indicator ';

    if (value === null || value === undefined) {
      className += 'status-gray';
    } else {
      const poorHigh = sensorThresholds?.carbon_dioxide_poor_high ?? 5000;
      const badHigh = sensorThresholds?.carbon_dioxide_bad_high ?? 2000;
      const fairHigh = sensorThresholds?.carbon_dioxide_fair_high ?? 1000;

      if (value > poorHigh) {
        className += 'status-red';
      } else if (value > badHigh) {
        className += 'status-orange';
      } else if (value > fairHigh) {
        className += 'status-yellow';
      } else {
        className += 'status-green';
      }
    }

    return this.getIndicator(className);
  }

  getNoiseStatusIndicator = (value, sensorThresholds) => {
    let className = 'status-indicator ';

    if (value === null || value === undefined) {
      className += 'status-gray';
    } else {
      const poorHigh = sensorThresholds?.noise_level_poor_high ?? 105;
      const badHigh = sensorThresholds?.noise_level_bad_high ?? 100;
      const fairHigh = sensorThresholds?.noise_level_fair_high ?? 95;

      if (value > poorHigh) {
        className += 'status-red';
      } else if (value > badHigh) {
        className += 'status-orange';
      } else if (value > fairHigh) {
        className += 'status-yellow';
      } else {
        className += 'status-green';
      }
    }

    return this.getIndicator(className);
  }

  temperatureBodyTemplate = (rowData) => {
    const { sensorDataThreshold } = this.state;
    const value = rowData[SENSOR_TYPE.TEMPERATURE];
    const displayValue = value !== null && value !== undefined ? parseFloat(value).toFixed(1) : '--';
    return (
      <div className="flex align-items-center">
        {this.getTemperatureStatusIndicator(value, sensorDataThreshold)}
        <span className="ml-2">{displayValue}</span>
      </div>
    );
  };

  humidityBodyTemplate = (rowData) => {
    const { sensorDataThreshold } = this.state;
    const value = rowData[SENSOR_TYPE.HUMIDITY];
    const displayValue = value !== null && value !== undefined ? parseFloat(value).toFixed(1) : '--';
    return (
      <div className="flex align-items-center">
        {this.getHumidityStatusIndicator(value, sensorDataThreshold)}
        <span className="ml-2">{displayValue}</span>
      </div>
    );
  };

  co2BodyTemplate = (rowData) => {
    const { sensorDataThreshold } = this.state;
    const value = rowData[SENSOR_TYPE.CARBON_DIOXIDE];
    const displayValue = value !== null && value !== undefined ? parseFloat(value).toFixed(1) : '--';
    return (
      <div className="flex align-items-center">
        {this.getCO2StatusIndicator(value, sensorDataThreshold)}
        <span className="ml-2">{displayValue}</span>
      </div>
    );
  };

  noiseBodyTemplate = (rowData) => {
    const { sensorDataThreshold } = this.state;
    const value = rowData[SENSOR_TYPE.NOISE_LEVEL];
    const displayValue = value !== null && value !== undefined ? parseFloat(value).toFixed(1) : '--';
    return (
      <div className="flex align-items-center">
        {this.getNoiseStatusIndicator(value, sensorDataThreshold)}
        <span className="ml-2">{displayValue}</span>
      </div>
    );
  };

  renderSensorMarkers = () => {
    const { sensorData, selectedSensorType, sensorDataThreshold } = this.state;

    let sensorDataTable = sensorData.map(item => {
      return item['data'];
    });

    // update color for each column
    sensorDataTable = this.updateColumnColor({
      sensorDataList: sensorDataTable,
      sensorThresholds: sensorDataThreshold,
    });

    const sensorDataMarkers = sensorDataTable?.map(item => {
      const { position_x, position_y } = item;
      const marker = transformPositionToMarkerCoordinate({
        x: position_x,
        y: position_y,
      });
      return {
        ...marker,
        item: item,
        keyItem: selectedSensorType,
      };
    });

    return sensorDataMarkers || [];
  };

  customSortIcon = (options) => {
    let iconSrc;

    if (options.sorted) {
      if (options.sortOrder < 0) {
        iconSrc = "/assets/img/sort-down-icon.svg"; // descending
      } else {
        iconSrc = "/assets/img/sort-up-icon.svg"; // ascending
      }
    } else {
      iconSrc = "/assets/img/sort-icon.svg"; // unsorted
    }

    return (
      <img
        src={iconSrc}
        alt="sort"
        className="custom-sort-icon"
        {...options.iconProps}
      />
    );
  };

  render() {
    const {
      isLoadingData,
      floorMapUrl,
      floorMapError,
      sensorData,
      selectedSensorType,
      sensorDataThreshold,
    } = this.state;
    let sensorDataTable = sensorData.map(item => {
      return item['data'];
    });
    // update color for each column
    sensorDataTable = this.updateColumnColor({
      sensorDataList: sensorDataTable,
      sensorThresholds: sensorDataThreshold,
    });

    if (isLoadingData) {
      return (
        <div className="air-quality-content">
          <LoadingSpinner />
        </div>
      );
    }

    if (this.renderMessageError()) {
      return (
        <div className="air-quality-content">
          {this.renderMessageError()}
        </div>
      );
    }

    const isFloorMapError = !floorMapUrl || floorMapError;

    return (
      <div className="air-quality-content">
        <div className="data-panel">
          <div className="sensor-table-container">
            <DataTable
              value={sensorDataTable}
              className="sensor-data-table"
              scrollable
              sortIcon={this.customSortIcon}
              emptyMessage={() => {
                return (
                  <div className="no-data-container">
                    {COMMON_TEXT.NO_DATA}
                  </div>
                );
              }}
            >
              <Column
                field="device_name"
                header="名前"
                sortable
                className="name-column"
                headerStyle={{ color: '#A2A8BB', fontFamily: '"DM Sans"', fontSize: '12px', fontStyle: 'normal', fontWeight: '700', lineHeight: 'normal', padding: '4px 0px' }}
                bodyStyle={{ color: '#FFF', fontFamily: '"DM Sans"', fontSize: '12px', fontStyle: 'normal', fontWeight: '700', lineHeight: '30px', padding: '4px 0px' }}
              />
              <Column
                field={SENSOR_TYPE.TEMPERATURE}
                header={
                  <>
                    <span>温度</span>
                    <br />
                    <span >(℃)</span>
                  </>
                }
                body={e => this.temperatureBodyTemplate(e)}
                sortable
                className="data-column"
                headerStyle={{ color: '#A2A8BB', fontFamily: '"DM Sans"', fontSize: '12px', fontStyle: 'normal', fontWeight: '700', lineHeight: 'normal', padding: '4px 0px' }}
                bodyStyle={{ color: '#FFF', fontFamily: '"DM Sans"', fontSize: '12px', fontStyle: 'normal', fontWeight: '700', lineHeight: '30px', padding: '4px 0px' }}
              />
              <Column
                field={SENSOR_TYPE.HUMIDITY}
                header={
                  <>
                    <span>湿度</span>
                    <br />
                    <span >(%)</span>
                  </>
                }
                body={e => this.humidityBodyTemplate(e)}
                sortable
                className="data-column"
                headerStyle={{ color: '#A2A8BB', fontFamily: '"DM Sans"', fontSize: '12px', fontStyle: 'normal', fontWeight: '700', lineHeight: 'normal', padding: '4px 0px' }}
                bodyStyle={{ color: '#FFF', fontFamily: '"DM Sans"', fontSize: '12px', fontStyle: 'normal', fontWeight: '700', lineHeight: '30px', padding: '4px 0px' }}
              />
              <Column
                field={SENSOR_TYPE.CARBON_DIOXIDE}
                header={
                  <>
                    <span>二酸化炭素</span>
                    <br />
                    <span >(ppm)</span>
                  </>
                }
                body={e => this.co2BodyTemplate(e)}
                sortable
                className="data-column"
                headerStyle={{ color: '#A2A8BB', fontFamily: '"DM Sans"', fontSize: '12px', fontStyle: 'normal', fontWeight: '700', lineHeight: 'normal', padding: '4px 0px' }}
                bodyStyle={{ color: '#FFF', fontFamily: '"DM Sans"', fontSize: '12px', fontStyle: 'normal', fontWeight: '700', lineHeight: '30px', padding: '4px 0px' }}
              />
              <Column
                field={SENSOR_TYPE.NOISE_LEVEL}
                header={
                  <>
                    <span>騒音</span>
                    <br />
                    <span >(dB)</span>
                  </>
                }
                body={e => this.noiseBodyTemplate(e)}
                sortable
                className="data-column"
                headerStyle={{ color: '#A2A8BB', fontFamily: '"DM Sans"', fontSize: '12px', fontStyle: 'normal', fontWeight: '700', lineHeight: 'normal', padding: '4px 0px' }}
                bodyStyle={{ color: '#FFF', fontFamily: '"DM Sans"', fontSize: '12px', fontStyle: 'normal', fontWeight: '700', lineHeight: '30px', padding: '4px 0px' }}
              />
            </DataTable>
          </div>
        </div>

        <div className="map-panel">
          <div className="measurement-tab-menu">
            <button
              className={`measurement-tab-btn${selectedSensorType === SENSOR_TYPE.TEMPERATURE ? ' selected' : ''}`}
              onClick={() => this.setState({ selectedSensorType: SENSOR_TYPE.TEMPERATURE })}
              type="button"
            >
              温度
            </button>
            <button
              className={`measurement-tab-btn${selectedSensorType === SENSOR_TYPE.HUMIDITY ? ' selected' : ''}`}
              onClick={() => this.setState({ selectedSensorType: SENSOR_TYPE.HUMIDITY })}
              type="button"
            >
              湿度
            </button>
            <button
              className={`measurement-tab-btn${selectedSensorType === SENSOR_TYPE.CARBON_DIOXIDE ? ' selected' : ''}`}
              onClick={() => this.setState({ selectedSensorType: SENSOR_TYPE.CARBON_DIOXIDE })}
              type="button"
            >
              二酸化炭素
            </button>
            <button
              className={`measurement-tab-btn${selectedSensorType === SENSOR_TYPE.NOISE_LEVEL ? ' selected' : ''}`}
              onClick={() => this.setState({ selectedSensorType: SENSOR_TYPE.NOISE_LEVEL })}
              type="button"
            >
              騒音
            </button>
          </div>

          <div className="map-container">
            {isFloorMapError ? (
              <div className="map-error">
                <Message
                  severity="error"
                  text={COMMON_TEXT.CANNOT_LOAD_FLOORMAP}
                />
              </div>
            ) : (
              <ImageMarker
                extraClass="custom-image-marker"
                bufferLeft={0}
                bufferTop={0}
                src={floorMapUrl}
                markerComponent={CustomSensorMarker}
                markers={this.renderSensorMarkers()}
                alt=""
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

AirQualityNow.propTypes = {};

export default AirQualityNow;
